import type { ReactNode } from "react";
import type { Row } from "../api";
import { Button, Badge, Callout, Empty, Progress } from "../ui";
import { ArrowRight, ClipboardList, FileText, ListChecks } from "lucide-react";
import { intakeProgress, draftNeedsAttention } from "../presentation";
import { activityLabel } from "../terminology";
import "./preparation-journey.css";
import { buildIntakeCards, intakeAnswerSummary } from "./IntakeExperience";

export const interviewSteps = [
  {
    label: "Personal information",
    path: "/intake/facts",
    matches: ["/intake/facts", "/intake/supplemental"],
  },
  {
    label: "Tax questions",
    path: "/intake/questionnaire",
    matches: ["/intake/questionnaire"],
  },
  {
    label: "W-2s & documents",
    path: "/intake/w2-upload",
    matches: [
      "/intake/w2-upload",
      "/intake/evidence-upload",
      "/intake/evidence-review",
      "/evidence/",
    ],
  },
  {
    label: "Check your answers",
    path: "/intake/reconciliation",
    matches: ["/intake/reconciliation", "/intake/admission"],
  },
  { label: "Preparer review", path: "/review/preparer", matches: ["/review/"] },
  { label: "Your return", path: "/forms/1040", matches: ["/forms/1040"] },
];
export function interviewStepIndex(path: string) {
  return interviewSteps.findIndex((step) =>
    step.matches.some((match) =>
      match.endsWith("/") ? path.startsWith(match) : path === match,
    ),
  );
}
export function InterviewJourney({
  path,
  navigate,
  footer = false,
}: {
  path: string;
  navigate: (path: string) => void;
  footer?: boolean;
}) {
  const index = interviewStepIndex(path);
  if (index < 0) return null;
  if (footer)
    return (
      <div className="interview-next">
        <span>
          Move between steps freely. Moving on does not save an answer or
          complete a review.
        </span>
        <div>
          {index > 0 && (
            <Button onClick={() => navigate(interviewSteps[index - 1].path)}>
              Back: {interviewSteps[index - 1].label}
            </Button>
          )}
          {index < interviewSteps.length - 1 && (
            <Button
              variant="primary"
              onClick={() => navigate(interviewSteps[index + 1].path)}
            >
              Next: {interviewSteps[index + 1].label}
              <ArrowRight size={15} />
            </Button>
          )}
        </div>
      </div>
    );
  return (
    <nav className="interview-journey" aria-label="Return preparation steps">
      <ol>
        {interviewSteps.map((step, i) => (
          <li key={step.path}>
            <button
              type="button"
              aria-current={index === i ? "step" : undefined}
              onClick={() => navigate(step.path)}
            >
              <span aria-hidden="true">{i + 1}</span>
              <strong>{step.label}</strong>
            </button>
          </li>
        ))}
      </ol>
      <p>
        Step {index + 1} of {interviewSteps.length} ·{" "}
        {interviewSteps[index].label}
      </p>
    </nav>
  );
}

export function PreparationOverview({
  user,
  workspace,
  navigate,
  children,
}: {
  user: Row;
  workspace: Row;
  navigate: (path: string) => void;
  children: ReactNode;
}) {
  const writable = ["workspace_admin", "preparer"].includes(user.role);
  return (
    <>
      <div className="dashboard-heading">
        <div>
          <p className="eyebrow">YOUR TAX PREPARATION WORKSPACE</p>
          <h1>
            Good to see you, {String(user.name).split(" ")[0]}
            <span className="green-period">.</span>
          </h1>
          <p>Gather information, add documents, and keep preparation moving.</p>
        </div>
        {writable && (
          <Button variant="primary" onClick={() => navigate("/intake/new")}>
            + New Return
          </Button>
        )}
      </div>
      <div className="release-banner">
        <ClipboardList size={24} />
        <div>
          <strong>Start with the taxpayer’s story.</strong>
          <span>
            Federal individual returns · Tax year 2025 · Synthetic practice data
            only
          </span>
        </div>
        <Badge>Draft preparation</Badge>
      </div>
      <div className="metric-grid">
        {[
          {
            label: "Active Returns",
            count: workspace.cases?.length || 0,
            path: "/workspace/cases",
            detail: "Continue a return",
          },
          {
            label: "Open tasks",
            count: (workspace.tasks || []).filter(
              (t: Row) => t.status !== "done",
            ).length,
            path: "/productivity/tasks",
            detail: "Keep track of next steps",
          },
        ].map((item) => (
          <button
            className="metric-card"
            key={item.label}
            onClick={() => navigate(item.path)}
          >
            <div>
              <span>{item.label}</span>
              <ListChecks size={17} />
            </div>
            <strong>{item.count}</strong>
            <p>
              {item.detail}
              <ArrowRight size={14} />
            </p>
          </button>
        ))}
      </div>
      <section className="card">
        <div className="card-heading">
          <h2>Your Returns</h2>
          <Button onClick={() => navigate("/workspace/cases")}>
            View all returns
          </Button>
        </div>
        {children}
      </section>
      <Callout title="A safe place to prepare">
        Use synthetic information only. Saving intake answers does not calculate
        tax, complete a review, or prepare a return for filing.
      </Callout>
    </>
  );
}

export function IntakeReview({
  questions,
  selected,
  navigate,
  reconciliation = false,
  choiceMasters = [],
}: {
  questions: Row[];
  selected: Row | null;
  navigate: (path: string) => void;
  reconciliation?: boolean;
  choiceMasters?: Row[];
}) {
  if (!selected)
    return (
      <Empty
        title="Select a return to continue"
        description="Choose a return above to review its information and documents."
      />
    );
  const answers = selected.answers || {},
    progress = intakeProgress(questions, answers);
  const { cards } = buildIntakeCards(
    { questions, choiceMasters },
    "questionnaire",
  );
  return (
    <>
      <section className="card form-card">
        <div className="form-card-icon">
          <ClipboardList size={24} />
        </div>
        <h2>
          {reconciliation
            ? "Review the information together."
            : "Your intake checklist"}
        </h2>
        <p>
          {progress.saved} of {progress.total} questions have saved answers.{" "}
          {progress.attention} still need an answer or follow-up.
        </p>
        <Progress
          value={progress.total ? (progress.saved / progress.total) * 100 : 0}
          label="Questions with saved answers"
        />
        <p>
          Saved answers are not verified answers. Compare them with the
          taxpayer’s documents and resolve any uncertainty.
        </p>
        <div className="form-actions">
          <Button
            variant="primary"
            onClick={() => navigate("/intake/questionnaire")}
          >
            Continue questions
          </Button>
          <Button onClick={() => navigate("/intake/evidence-review")}>
            Review documents
          </Button>
        </div>
      </section>
      <div className="comparison-grid">
        <section className="card form-card">
          <h2>Questions needing attention</h2>
          {questions.filter(
            (q) => q.stage === "PRECALC" && draftNeedsAttention(answers[q.id]),
          ).length ? (
            <p>
              Return to the questions to complete missing answers and resolve
              any responses marked “Not sure” or “Information differs.”
            </p>
          ) : (
            <p>
              All intake questions have responses without an unresolved marker.
              They still need review; this is not a tax eligibility decision.
            </p>
          )}
        </section>
        <section className="card form-card">
          <h2>Preparation and review</h2>
          <p>
            Calculations and independent checks have not been completed.{" "}
            {progress.later} additional checks belong to later preparation and
            review steps.
          </p>
          <Badge>Not ready to finalize</Badge>
        </section>
      </div>
      <section
        className="card form-card"
        aria-label="Saved interview responses"
      >
        <h2>Your interview responses</h2>
        <p>
          These are saved draft responses, not calculated results. Return to Tax
          questions to make changes.
        </p>
        <dl className="zt-property-list">
          {cards.map((card) => (
            <div key={card.record.id}>
              <dt>{card.title}</dt>
              <dd>
                {intakeAnswerSummary(answers[card.record.id], card.schema)}
              </dd>
            </div>
          ))}
        </dl>
      </section>
    </>
  );
}

export function PreparationRecordSummary({
  record,
  returnId,
}: {
  record: Row;
  returnId?: string;
}) {
  const fields = [
    ["Return ID", returnId],
    ["Tax year", record.taxYear],
    ["Status", record.status],
    ["Assigned to", record.assignee],
    ["Document", record.mimeType ? record.name : undefined],
    ["File type", record.mimeType],
    [
      "File size",
      typeof record.size === "number"
        ? `${Math.ceil(record.size / 1024)} KB`
        : undefined,
    ],
    ["Activity", record.action ? activityLabel(record.action) : undefined],
    ["Recorded by", record.actor],
    [
      "Added",
      record.createdAt
        ? new Date(record.createdAt).toLocaleDateString()
        : undefined,
    ],
  ].filter(([, value]) => value !== undefined && value !== null);
  return (
    <div className="detail-body">
      <h3>{record.name || record.title || "Record overview"}</h3>
      {fields.length ? (
        <dl className="zt-property-list">
          {fields.map(([label, value]) => (
            <div key={String(label)}>
              <dt>{label}</dt>
              <dd>{String(value)}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <p>
          This record supports preparation. Detailed system information is kept
          separate from the preparation view.
        </p>
      )}
      <Callout title="Preparation record">
        Information saved here remains a draft until the required review has
        been completed.
      </Callout>
    </div>
  );
}

export function FormPreparationStatus({
  navigate,
}: {
  navigate: (path: string) => void;
}) {
  return (
    <section className="card form-card">
      <FileText size={28} />
      <h2>Your Form 1040 will appear here.</h2>
      <p>
        No completed return has been generated. Continue gathering taxpayer
        information and documents; calculation and form preparation are not yet
        available in this local application.
      </p>
      <Button
        variant="primary"
        onClick={() => navigate("/intake/questionnaire")}
      >
        Continue intake
      </Button>
    </section>
  );
}
